import {
  Alert,
  Button,
  Checkbox,
  Divider,
  FormControl,
  FormControlLabel,
  HairlineCard,
  IconButton,
  Icons,
  iconSize,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  StatusChip,
  TextField,
  Typography,
} from '@munifocus/ui';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import type {
  CreateServiceRecordDto,
  MailClass,
  ServedRelationship,
  ServiceMethod,
  ServiceRecordDetail,
} from '@munifocus/contracts-gov';
import { MAIL_CLASSES, SERVED_RELATIONSHIPS, SERVICE_METHODS } from '@munifocus/contracts-gov';
import {
  useCreateServiceRecord,
  useDeleteServiceRecord,
  useServiceRecords,
  useUpdateServiceRecord,
} from '../lib/service-records-api';
import { useConfirmAction } from '../lib/useConfirmAction';
import { useSubmitValidation } from '../lib/useSubmitValidation';

/**
 * Step 5.3 — service of the Form 4. Each method carries its own particulars, and
 * the file keeps every attempt: a failed one (mail returned, nobody home) stays
 * on the evidentiary record next to the successful one. Exactly one record is
 * operative — the date that starts the 14-day appeal clock (s. 134(1)).
 */

/**
 * The method-specific detail lines shown on a saved record. Falls back to an
 * empty list rather than `undefined` so an unexpected method (older API, hand-
 * edited data) renders a bare row instead of throwing.
 */
function methodDetail(r: ServiceRecordDetail): string[] {
  switch (r.method) {
    case 'posted':
      return [r.postingLocation].filter(Boolean) as string[];
    case 'in_person':
      return [r.personName, r.personRelationship, r.serviceLocation].filter(Boolean) as string[];
    case 'mail':
      return [r.mailingAddress, r.mailClass, r.trackingNumber].filter(Boolean) as string[];
    case 'email':
      return [r.emailAddress, r.deliveryConfirmation].filter(Boolean) as string[];
    default:
      return [];
  }
}

function RecordRow({ record, fileId }: { record: ServiceRecordDetail; fileId: string }) {
  const { t } = useTranslation();
  const update = useUpdateServiceRecord(fileId);
  const remove = useDeleteServiceRecord(fileId);
  const confirm = useConfirmAction();
  const busy = update.isPending || remove.isPending;

  return (
    <Stack spacing={0.5} sx={{ py: 1 }}>
      <Stack direction="row" sx={{ alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
        <Typography variant="body2">{t(`gov.service.method.${record.method}`)}</Typography>
        <Typography variant="caption" color="text.secondary">
          {record.serviceDate}
          {record.serviceTime ? ` · ${record.serviceTime}` : ''}
        </Typography>
        {record.operative && <StatusChip status="success" label={t('gov.service.operative')} />}
        {!record.successful && <StatusChip status="warning" label={t('gov.service.failed')} />}
        <Stack direction="row" sx={{ ml: 'auto', gap: 0.5 }}>
          {/* Only a successful record can carry the statutory clock. */}
          {record.successful && !record.operative && (
            <Button
              size="small"
              disabled={busy}
              onClick={() => update.mutate({ id: record.id, dto: { operative: true } })}
            >
              {t('gov.service.makeOperative')}
            </Button>
          )}
          <IconButton
            size="small"
            aria-label={t('gov.service.remove')}
            disabled={busy}
            onClick={async () => {
              if (
                !(await confirm({
                  title: t('gov.service.removeTitle'),
                  body: t('gov.service.removeBody', {
                    method: t(`gov.service.method.${record.method}`),
                    date: record.serviceDate,
                  }),
                  confirmLabel: t('gov.service.removeConfirm'),
                  tone: 'danger',
                }))
              ) {
                return;
              }
              remove.mutate(record.id);
            }}
          >
            <Icons.Trash2 size={iconSize.md} />
          </IconButton>
        </Stack>
      </Stack>
      {methodDetail(record).length > 0 && (
        <Typography variant="caption" color="text.secondary">
          {methodDetail(record).join(' · ')}
        </Typography>
      )}
      {record.notes && <Typography variant="caption">{record.notes}</Typography>}
    </Stack>
  );
}

const EMPTY: CreateServiceRecordDto = { method: 'posted', serviceDate: '' };

function AddServiceForm({ fileId, onDone }: { fileId: string; onDone: () => void }) {
  const { t } = useTranslation();
  const create = useCreateServiceRecord(fileId);
  const [dto, setDto] = useState<CreateServiceRecordDto>(EMPTY);

  const set = (patch: Partial<CreateServiceRecordDto>) => setDto((p) => ({ ...p, ...patch }));
  const v = useSubmitValidation({
    serviceDate: dto.serviceDate ? null : t('common.validation.required'),
  });

  // Hiding an input must also drop its value, or a field the officer can no
  // longer see still gets submitted (mail has no time; regular mail has no
  // tracking number). The server normalises too — this keeps the form honest.
  const setMethod = (method: ServiceMethod) =>
    set({ method, ...(method === 'mail' ? { serviceTime: null } : {}) });

  const setMailClass = (mailClass: MailClass | null) =>
    set({ mailClass, ...(mailClass === 'regular' || !mailClass ? { trackingNumber: null } : {}) });

  const submit = () =>
    create.mutate(dto, {
      onSuccess: () => {
        setDto(EMPTY);
        onDone();
      },
    });

  return (
    <Stack spacing={2} sx={{ mt: 1 }}>
      {create.isError && <Alert severity="error">{t('gov.service.saveError')}</Alert>}

      <FormControl fullWidth size="small">
        <InputLabel>{t('gov.service.methodLabel')}</InputLabel>
        <Select
          label={t('gov.service.methodLabel')}
          aria-label={t('gov.service.methodLabel')}
          value={dto.method}
          onChange={(e) => setMethod(e.target.value as ServiceMethod)}
        >
          {SERVICE_METHODS.map((m) => (
            <MenuItem key={m} value={m}>
              {t(`gov.service.method.${m}`)}
            </MenuItem>
          ))}
        </Select>
      </FormControl>

      <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
        <TextField
          fullWidth
          size="small"
          required
          type="date"
          label={t('gov.service.date')}
          {...v.field('serviceDate')}
          slotProps={{ inputLabel: { shrink: true } }}
          value={dto.serviceDate}
          onChange={(e) => set({ serviceDate: e.target.value })}
          helperText={v.errors.serviceDate ?? t('gov.service.dateHelp')}
        />
        {/* Mail records a date only; the other methods record a time too. */}
        {dto.method !== 'mail' && (
          <TextField
            fullWidth
            size="small"
            type="time"
            label={t('gov.service.time')}
            slotProps={{ inputLabel: { shrink: true } }}
            value={dto.serviceTime ?? ''}
            onChange={(e) => set({ serviceTime: e.target.value || null })}
          />
        )}
      </Stack>

      {/* ── Method-specific particulars ──────────────────────────────────── */}
      {dto.method === 'posted' && (
        <TextField
          fullWidth
          size="small"
          label={t('gov.service.postingLocation')}
          helperText={t('gov.service.postingLocationHelp')}
          value={dto.postingLocation ?? ''}
          onChange={(e) => set({ postingLocation: e.target.value || null })}
        />
      )}

      {dto.method === 'in_person' && (
        <>
          <TextField
            fullWidth
            size="small"
            label={t('gov.service.personName')}
            value={dto.personName ?? ''}
            onChange={(e) => set({ personName: e.target.value || null })}
          />
          <FormControl fullWidth size="small">
            <InputLabel>{t('gov.service.relationship')}</InputLabel>
            <Select
              label={t('gov.service.relationship')}
              aria-label={t('gov.service.relationship')}
              value={dto.personRelationship ?? ''}
              onChange={(e) =>
                set({ personRelationship: (e.target.value || null) as ServedRelationship })
              }
            >
              {SERVED_RELATIONSHIPS.map((r) => (
                <MenuItem key={r} value={r}>
                  {t(`gov.service.relationshipOption.${r}`)}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <TextField
            fullWidth
            size="small"
            label={t('gov.service.serviceLocation')}
            value={dto.serviceLocation ?? ''}
            onChange={(e) => set({ serviceLocation: e.target.value || null })}
          />
        </>
      )}

      {dto.method === 'mail' && (
        <>
          <TextField
            fullWidth
            size="small"
            label={t('gov.service.mailingAddress')}
            value={dto.mailingAddress ?? ''}
            onChange={(e) => set({ mailingAddress: e.target.value || null })}
          />
          <FormControl fullWidth size="small">
            <InputLabel>{t('gov.service.mailClass')}</InputLabel>
            <Select
              label={t('gov.service.mailClass')}
              aria-label={t('gov.service.mailClass')}
              value={dto.mailClass ?? ''}
              onChange={(e) => setMailClass((e.target.value || null) as MailClass)}
            >
              {MAIL_CLASSES.map((c) => (
                <MenuItem key={c} value={c}>
                  {t(`gov.service.mailClassOption.${c}`)}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          {/* Tracking only exists for registered/certified mail. */}
          {dto.mailClass && dto.mailClass !== 'regular' && (
            <TextField
              fullWidth
              size="small"
              label={t('gov.service.trackingNumber')}
              value={dto.trackingNumber ?? ''}
              onChange={(e) => set({ trackingNumber: e.target.value || null })}
            />
          )}
        </>
      )}

      {dto.method === 'email' && (
        <>
          <TextField
            fullWidth
            size="small"
            type="email"
            label={t('gov.service.emailAddress')}
            value={dto.emailAddress ?? ''}
            onChange={(e) => set({ emailAddress: e.target.value || null })}
          />
          <TextField
            fullWidth
            size="small"
            label={t('gov.service.deliveryConfirmation')}
            value={dto.deliveryConfirmation ?? ''}
            onChange={(e) => set({ deliveryConfirmation: e.target.value || null })}
          />
        </>
      )}

      <TextField
        fullWidth
        size="small"
        multiline
        minRows={2}
        label={t('gov.service.notes')}
        value={dto.notes ?? ''}
        onChange={(e) => set({ notes: e.target.value || null })}
      />

      <FormControlLabel
        control={
          <Checkbox
            checked={dto.successful ?? true}
            onChange={(e) => set({ successful: e.target.checked })}
          />
        }
        label={t('gov.service.successful')}
      />
      {dto.successful === false && (
        <Alert severity="info" sx={{ py: 0 }}>
          {t('gov.service.failedNote')}
        </Alert>
      )}

      <Stack direction="row" spacing={1}>
        <Button variant="contained" disabled={create.isPending} onClick={v.guard(submit)}>
          {t('gov.service.save')}
        </Button>
        <Button disabled={create.isPending} onClick={onDone}>
          {t('common.actions.cancel')}
        </Button>
      </Stack>
    </Stack>
  );
}

export function ServiceRecords({ fileId }: { fileId: string }) {
  const { t } = useTranslation();
  const { data, isError } = useServiceRecords(fileId);
  const [adding, setAdding] = useState(false);

  const records = data ?? [];

  return (
    <HairlineCard sx={{ p: 2 }}>
      <Stack direction="row" sx={{ alignItems: 'baseline', justifyContent: 'space-between' }}>
        <Typography variant="overline">{t('gov.service.title')}</Typography>
        <Typography variant="caption" color="text.secondary">
          {t('gov.service.statuteRef')}
        </Typography>
      </Stack>

      {isError && <Alert severity="error">{t('gov.service.loadError')}</Alert>}

      {records.length === 0 && !adding && (
        <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
          {t('gov.service.empty')}
        </Typography>
      )}

      {records.length > 0 && (
        <Stack divider={<Divider flexItem />} sx={{ mt: 1 }}>
          {records.map((r) => (
            <RecordRow key={r.id} record={r} fileId={fileId} />
          ))}
        </Stack>
      )}

      {adding ? (
        <AddServiceForm fileId={fileId} onDone={() => setAdding(false)} />
      ) : (
        <Button
          size="small"
          sx={{ mt: 1 }}
          startIcon={<Icons.Plus size={iconSize.md} />}
          onClick={() => setAdding(true)}
        >
          {t('gov.service.add')}
        </Button>
      )}
    </HairlineCard>
  );
}
